import React, { useEffect, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis } from 'recharts';
import iconSpinVideo from '../assets/branding/icon_spin.mp4';
import ModelLeagueStats from '../components/ModelLeagueStats';
import { useTranslation } from '../contexts/TranslationContext';
import '../styles/philip-snat-stats.scss';
import { apiGet } from '../utils/api';

interface LeagueStats {
  total_games: number;
  correct_picks: number;
  accuracy_percentage: number;
  stats_start_date: string | null;
}

interface StatsResponse {
  nhl: LeagueStats;
  khl: LeagueStats;
}

interface GameWithPrediction {
  id: number;
  date: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  predicted_winner: string;
  actual_winner: string;
  is_correct: boolean;
  prediction_goals: { [key: string]: number } | null;
  formatted_game: string | null;
  prediction_winner_prob: number | null;
}

interface PaginatedGamesResponse {
  games: GameWithPrediction[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const PhilipSnatStats: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [nhlGames, setNhlGames] = useState<PaginatedGamesResponse | null>(null);
  const [khlGames, setKhlGames] = useState<PaginatedGamesResponse | null>(null);
  const [loadingNhlGames, setLoadingNhlGames] = useState(false);
  const [loadingKhlGames, setLoadingKhlGames] = useState(false);
  const [nhlPage, setNhlPage] = useState(1);
  const [khlPage, setKhlPage] = useState(1);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiGet<StatsResponse>('/philip-snat/nhl/stats');
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const fetchNhlGames = async (page: number = 1) => {
    setLoadingNhlGames(true);
    try {
      const data = await apiGet<PaginatedGamesResponse>(
        `/philip-snat/nhl/games?page=${page}&page_size=10`
      );
      setNhlGames(data);
      setNhlPage(page);
    } catch (err) {
      console.error('Failed to fetch NHL games:', err);
    } finally {
      setLoadingNhlGames(false);
    }
  };

  const fetchKhlGames = async (page: number = 1) => {
    setLoadingKhlGames(true);
    try {
      const data = await apiGet<PaginatedGamesResponse>(
        `/philip-snat/khl/games?page=${page}&page_size=10`
      );
      setKhlGames(data);
      setKhlPage(page);
    } catch (err) {
      console.error('Failed to fetch KHL games:', err);
    } finally {
      setLoadingKhlGames(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const prepareChartData = (
    predictionGoals: { [key: string]: number } | null,
    actualGoals: number | null
  ) => {
    if (!predictionGoals) return [];
    return Object.entries(predictionGoals)
      .map(([goals, prob]) => ({
        goals: parseInt(goals),
        probability: prob,
      }))
      .sort((a, b) => a.goals - b.goals)
      .map((item) => ({
        name: item.goals.toString(),
        value: item.probability,
        isActual: actualGoals !== null && item.goals === actualGoals,
      }));
  };

  const renderFormattedGame = (formattedGame: string) => {
    const parts = formattedGame.split(/(\s(?:OT|SO)\s)/);
    return (
      <>
        {parts.map((part, index) => {
          if (part === ' OT ' || part === ' SO ') {
            return (
              <span key={index} className="philip-snat-stats__game-ot-so">
                {part}
              </span>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  if (loading) {
    return (
      <div className="philip-snat-stats">
        <div className="philip-snat-stats__container">
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="philip-snat-stats">
      <div className="philip-snat-stats__container">
        <div className="philip-snat-stats__header">
          <h1 className="philip-snat-stats__title">{t.philipSnat.predictionStats}</h1>
          <button
            className="button_primary philip-snat-stats__back-button"
            onClick={handleBack}
          >
            {t.common.back}
          </button>
        </div>

        {stats && stats.nhl && stats.khl ? (
          <>
            <ModelLeagueStats
              leagueName="NHL"
              stats={stats.nhl}
              games={nhlGames}
              loadingGames={loadingNhlGames}
              onShowGames={() => fetchNhlGames(1)}
              onHideGames={() => setNhlGames(null)}
            />

            {loadingNhlGames && (
                <div className="philip-snat-stats__loading">
                  <video
                    className="philip-snat-stats__loading-icon"
                    src={iconSpinVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                </div>
              )}

              {nhlGames && !loadingNhlGames && (
                <div className="philip-snat-stats__games-list">
                  {nhlGames.games.map((game) => {
                    const isHomeWinner = game.actual_winner.toLowerCase() === 'home';
                    const actualGoals =
                      game.home_score !== null && game.away_score !== null
                        ? game.home_score + game.away_score
                        : null;
                    const chartData = prepareChartData(game.prediction_goals, actualGoals);

                    let homePercent = 0;
                    let awayPercent = 0;
                    if (game.prediction_winner_prob !== null) {
                      awayPercent = game.prediction_winner_prob;
                      homePercent = 1 - game.prediction_winner_prob;
                    }

                    const homeScore = game.home_score;
                    const awayScore = game.away_score;
                    const formattedGame = game.formatted_game || '';
                    const isOT = formattedGame.includes(' OT') || formattedGame.includes(' SO');
                    const isSO = formattedGame.includes(' SO');

                    let displayHomeScore: number | string | null = homeScore;
                    let displayAwayScore: number | string | null = awayScore;
                    if (isOT && homeScore !== null && awayScore !== null) {
                      if (isHomeWinner) {
                        displayHomeScore = `(${homeScore})`;
                      } else {
                        displayAwayScore = `(${awayScore})`;
                      }
                    }

                    return (
                      <div key={game.id} className="philip-snat-stats__game-item">
                        <div className="philip-snat-stats__game-main">
                          <div className="philip-snat-stats__game-info">
                            {game.is_correct ? (
                              <FaCheckCircle className="philip-snat-stats__game-icon philip-snat-stats__game-icon--correct" />
                            ) : (
                              <FiX className="philip-snat-stats__game-icon philip-snat-stats__game-icon--incorrect" />
                            )}
                            <span className="philip-snat-stats__game-date">
                              {formatDate(game.date)}.
                            </span>{' '}
                            <span className={`philip-snat-stats__game-team-name ${
                              isHomeWinner ? 'philip-snat-stats__game-team-name--winner' : ''
                            }`}>
                              {game.home_team}
                            </span>{' '}
                            <span className={`philip-snat-stats__game-score ${
                              isHomeWinner ? 'philip-snat-stats__game-score--winner' : ''
                            }`}>
                              {displayHomeScore !== null ? String(displayHomeScore) : t.parlaySummary.na}
                            </span>{' '}
                            -{' '}
                            <span className={`philip-snat-stats__game-score ${
                              !isHomeWinner ? 'philip-snat-stats__game-score--winner' : ''
                            }`}>
                              {displayAwayScore !== null ? String(displayAwayScore) : t.parlaySummary.na}
                            </span>
                            {(isOT || isSO) && (
                              <span className="philip-snat-stats__game-ot-so">
                                {' '}{isSO ? 'SO' : 'OT'}
                              </span>
                            )}{' '}
                            <span className={`philip-snat-stats__game-team-name ${
                              !isHomeWinner ? 'philip-snat-stats__game-team-name--winner' : ''
                            }`}>
                              {game.away_team}
                            </span>
                          </div>
                          {game.prediction_winner_prob !== null && (
                            <div className="philip-snat-stats__game-probability">
                              <div className="philip-snat-stats__probability-bar">
                                <div
                                  className="philip-snat-stats__probability-segment philip-snat-stats__probability-segment--home"
                                  style={{ width: `${homePercent * 100}%` }}
                                >
                                  {homePercent > 0.1 && (
                                    <span className="philip-snat-stats__probability-label">
                                      {(homePercent * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="philip-snat-stats__probability-segment philip-snat-stats__probability-segment--away"
                                  style={{ width: `${awayPercent * 100}%` }}
                                >
                                  {awayPercent > 0.1 && (
                                    <span className="philip-snat-stats__probability-label">
                                      {(awayPercent * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {chartData.length > 0 && (
                          <div className="philip-snat-stats__game-chart">
                            <ResponsiveContainer width="100%" height={60}>
                              <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: -10, left: 0 }}>
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                  {chartData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={entry.isActual ? '#f85149' : '#58C9FA'}
                                    />
                                  ))}
                                </Bar>
                                <XAxis
                                  dataKey="name"
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 10, fill: 'var(--color-text-secondary)' }}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {nhlGames.total_pages > 1 && (
                    <div className="philip-snat-stats__pagination">
                      <button
                        className="button_secondary philip-snat-stats__pagination-button"
                        onClick={() => fetchNhlGames(nhlPage - 1)}
                        disabled={nhlPage === 1}
                      >
                        {t.common.previous}
                      </button>
                      <span className="philip-snat-stats__pagination-info">
                        {t.common.paginationShort
                          .replace('{page}', String(nhlPage))
                          .replace('{totalPages}', String(nhlGames.total_pages))}
                      </span>
                      <button
                        className="button_secondary philip-snat-stats__pagination-button"
                        onClick={() => fetchNhlGames(nhlPage + 1)}
                        disabled={nhlPage === nhlGames.total_pages}
                      >
                        {t.common.next}
                      </button>
                    </div>
                  )}
                </div>
              )}

            <ModelLeagueStats
              leagueName="KHL"
              stats={stats.khl}
              games={khlGames}
              loadingGames={loadingKhlGames}
              onShowGames={() => fetchKhlGames(1)}
              onHideGames={() => setKhlGames(null)}
            />

            {loadingKhlGames && (
                <div className="philip-snat-stats__loading">
                  <video
                    className="philip-snat-stats__loading-icon"
                    src={iconSpinVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                </div>
              )}

              {khlGames && !loadingKhlGames && (
                <div className="philip-snat-stats__games-list">
                  {khlGames.games.map((game) => {
                    const isHomeWinner = game.actual_winner.toLowerCase() === 'home';
                    const actualGoals =
                      game.home_score !== null && game.away_score !== null
                        ? game.home_score + game.away_score
                        : null;
                    const chartData = prepareChartData(game.prediction_goals, actualGoals);

                    let homePercent = 0;
                    let awayPercent = 0;
                    if (game.prediction_winner_prob !== null) {
                      awayPercent = game.prediction_winner_prob;
                      homePercent = 1 - game.prediction_winner_prob;
                    }

                    const homeScore = game.home_score;
                    const awayScore = game.away_score;

                    return (
                      <div key={game.id} className="philip-snat-stats__game-item">
                        <div className="philip-snat-stats__game-main">
                          <div className="philip-snat-stats__game-info">
                            {game.is_correct ? (
                              <FaCheckCircle className="philip-snat-stats__game-icon philip-snat-stats__game-icon--correct" />
                            ) : (
                              <FiX className="philip-snat-stats__game-icon philip-snat-stats__game-icon--incorrect" />
                            )}
                            <span className="philip-snat-stats__game-date">
                              {formatDate(game.date)}.
                            </span>{' '}
                            <span className={`philip-snat-stats__game-team-name ${
                              isHomeWinner ? 'philip-snat-stats__game-team-name--winner' : ''
                            }`}>
                              {game.home_team}
                            </span>{' '}
                            <span className={`philip-snat-stats__game-score ${
                              isHomeWinner ? 'philip-snat-stats__game-score--winner' : ''
                            }`}>
                              {homeScore !== null ? String(homeScore) : t.parlaySummary.na}
                            </span>{' '}
                            -{' '}
                            <span className={`philip-snat-stats__game-score ${
                              !isHomeWinner ? 'philip-snat-stats__game-score--winner' : ''
                            }`}>
                              {awayScore !== null ? String(awayScore) : t.parlaySummary.na}
                            </span>{' '}
                            <span className={`philip-snat-stats__game-team-name ${
                              !isHomeWinner ? 'philip-snat-stats__game-team-name--winner' : ''
                            }`}>
                              {game.away_team}
                            </span>
                          </div>
                          {game.prediction_winner_prob !== null && (
                            <div className="philip-snat-stats__game-probability">
                              <div className="philip-snat-stats__probability-bar">
                                <div
                                  className="philip-snat-stats__probability-segment philip-snat-stats__probability-segment--home"
                                  style={{ width: `${homePercent * 100}%` }}
                                >
                                  {homePercent > 0.1 && (
                                    <span className="philip-snat-stats__probability-label">
                                      {(homePercent * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="philip-snat-stats__probability-segment philip-snat-stats__probability-segment--away"
                                  style={{ width: `${awayPercent * 100}%` }}
                                >
                                  {awayPercent > 0.1 && (
                                    <span className="philip-snat-stats__probability-label">
                                      {(awayPercent * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {chartData.length > 0 && (
                          <div className="philip-snat-stats__game-chart">
                            <ResponsiveContainer width="100%" height={60}>
                              <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: -10, left: 0 }}>
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                  {chartData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={entry.isActual ? '#f85149' : '#58C9FA'}
                                    />
                                  ))}
                                </Bar>
                                <XAxis
                                  dataKey="name"
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fontSize: 10, fill: 'var(--color-text-secondary)' }}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {khlGames.total_pages > 1 && (
                    <div className="philip-snat-stats__pagination">
                      <button
                        className="button_secondary philip-snat-stats__pagination-button"
                        onClick={() => fetchKhlGames(khlPage - 1)}
                        disabled={khlPage === 1}
                      >
                        {t.common.previous}
                      </button>
                      <span className="philip-snat-stats__pagination-info">
                        {t.common.paginationShort
                          .replace('{page}', String(khlPage))
                          .replace('{totalPages}', String(khlGames.total_pages))}
                      </span>
                      <button
                        className="button_secondary philip-snat-stats__pagination-button"
                        onClick={() => fetchKhlGames(khlPage + 1)}
                        disabled={khlPage === khlGames.total_pages}
                      >
                        {t.common.next}
                      </button>
                    </div>
                  )}
                </div>
              )}
          </>
        ) : (
          <p className="philip-snat-stats__empty">{t.philipSnat.noStatsAvailable}</p>
        )}
      </div>
    </div>
  );
};

export default PhilipSnatStats;
